import { useState, useRef, useCallback, ChangeEvent, RefObject } from 'react';
import Papa from 'papaparse';
import { REQUIRED_COLUMNS } from '../utils/csvHelpers';
import { validateRow, buildValidationLookups } from '../utils/bulkUploadValidation';
import type { CsvRow, ValidatedRow, NamedEntity } from '../types/bulkUpload';

const MAX_ROWS = 500;

interface UseCsvValidationReturn {
  file: File | null;
  fileError: string | null;
  validRows: ValidatedRow[];
  errorRows: ValidatedRow[];
  fileInputRef: RefObject<HTMLInputElement>;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  resetFile: () => void;
}

const useCsvValidation = (
  availableRoles: NamedEntity[],
  availableFeatures: NamedEntity[],
  availableBranches: NamedEntity[],
  availableTags: NamedEntity[],
  onValidated: () => void,
): UseCsvValidationReturn => {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [validRows, setValidRows] = useState<ValidatedRow[]>([]);
  const [errorRows, setErrorRows] = useState<ValidatedRow[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileReaderRef = useRef<FileReader | null>(null);
  const readTokenRef = useRef(0);

  const resetFile = () => {
    readTokenRef.current += 1;
    fileReaderRef.current?.abort();
    setFile(null);
    setValidRows([]);
    setErrorRows([]);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const failValidation = (message: string) => {
    setFileError(message);
    setValidRows([]);
    setErrorRows([]);
  };

  const processFile = useCallback((selectedFile: File) => {
    readTokenRef.current += 1;
    const readToken = readTokenRef.current;
    fileReaderRef.current?.abort();

    const reader = new FileReader();
    fileReaderRef.current = reader;
    reader.onerror = () => {
      if (reader.error?.name === 'AbortError' || readToken !== readTokenRef.current) return;
      failValidation('Failed to read the file. Please try again.');
    };
    reader.onload = (e) => {
      if (readToken !== readTokenRef.current) return;
      const csvText = e.target?.result as string;
      const result = Papa.parse<CsvRow>(csvText, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header: string) => header.trim().toLowerCase(),
      });

      if (result.errors.length > 0) {
        const parseErrors = result.errors
          .slice(0, 5)
          .map((err) => err.row !== undefined ? `Row ${err.row + 1}: ${err.message}` : err.message)
          .join('\n');
        failValidation(`The CSV file is malformed:\n${parseErrors}`);
        return;
      }

      const headers = result.meta.fields || [];
      const missingColumns = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));

      if (missingColumns.length > 0) {
        failValidation(`Missing required columns: ${missingColumns.join(', ')}. Please use the template.`);
        return;
      }

      if (result.data.length === 0) {
        failValidation('The CSV file contains no data rows.');
        return;
      }

      if (result.data.length > MAX_ROWS) {
        failValidation(`Too many rows (${result.data.length}). Maximum is ${MAX_ROWS} rows per upload.`);
        return;
      }

      const seenMobiles = new Set<string>();
      const seenEmails = new Set<string>();
      const lookups = buildValidationLookups(availableRoles, availableFeatures, availableBranches, availableTags);
      const validated = result.data.map((row, i) =>
        validateRow(row, i, seenMobiles, seenEmails, lookups)
      );
      setValidRows(validated.filter((r) => r.valid));
      setErrorRows(validated.filter((r) => !r.valid));
      onValidated();
    };

    reader.readAsText(selectedFile);
  }, [availableRoles, availableFeatures, availableBranches, availableTags, onValidated]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(null);
    setValidRows([]);
    setErrorRows([]);

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFileError('Please upload a CSV file.');
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFileError('File size must be less than 5MB.');
      return;
    }

    setFileError(null);
    setFile(selectedFile);
    processFile(selectedFile);
  };

  return {
    file,
    fileError,
    validRows,
    errorRows,
    fileInputRef,
    handleFileChange,
    resetFile,
  };
};

export default useCsvValidation;
